import type { ThemeConfig } from 'antd';
import type { ThemeAccent, ThemeMode } from '@appTypes/common.types';

const fontFamily =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

const lightNeutralToken = {
  colorSuccess: '#10b981',
  colorWarning: '#f59e0b',
  colorError: '#ef4444',
  colorBgBase: '#f8fafc',
  colorBgContainer: '#ffffff',
  colorBgElevated: '#ffffff',
  colorBgLayout: '#f1f5f9',
  colorText: '#0f172a',
  colorTextSecondary: '#475569',
  colorBorder: '#e2e8f0',
  colorBorderSecondary: '#f1f5f9',
  borderRadius: 8,
  fontFamily,
} as const;

const darkNeutralToken = {
  colorSuccess: '#10b981',
  colorWarning: '#fbbf24',
  colorError: '#ef4444',
  colorBgBase: '#0f172a',
  colorBgContainer: '#1e293b',
  colorBgElevated: '#1e293b',
  colorBgLayout: '#0f172a',
  colorText: '#f1f5f9',
  colorTextSecondary: '#94a3b8',
  colorBorder: '#334155',
  colorBorderSecondary: '#1e293b',
  borderRadius: 8,
  fontFamily,
} as const;

const lightAccent = {
  blue: {
    colorPrimary: '#2563eb',
    colorInfo: '#0891b2',
    menu: {
      itemSelectedBg: '#dbeafe',
      itemSelectedColor: '#1d4ed8',
      itemHoverBg: '#f1f5f9',
    },
  },
  orange: {
    colorPrimary: '#ea580c',
    colorInfo: '#c2410c',
    menu: {
      itemSelectedBg: '#ffedd5',
      itemSelectedColor: '#c2410c',
      itemHoverBg: '#fff7ed',
    },
  },
  sky: {
    colorPrimary: '#0284c7',
    colorInfo: '#0ea5e9',
    menu: {
      itemSelectedBg: '#bae6fd',
      itemSelectedColor: '#0369a1',
      itemHoverBg: '#e0f2fe',
    },
  },
  rose: {
    colorPrimary: '#e11d48',
    colorInfo: '#f43f5e',
    menu: {
      itemSelectedBg: '#fecdd3',
      itemSelectedColor: '#be123c',
      itemHoverBg: '#ffe4e6',
    },
  },
} satisfies Record<
  ThemeAccent,
  {
    colorPrimary: string;
    colorInfo: string;
    menu: { itemSelectedBg: string; itemSelectedColor: string; itemHoverBg: string };
  }
>;

const darkAccent = {
  blue: {
    colorPrimary: '#6366f1',
    colorInfo: '#0ea5e9',
    menu: {
      itemSelectedBg: 'rgba(99, 102, 241, 0.32)',
      itemSelectedColor: '#a5b4fc',
      itemHoverBg: 'rgba(99, 102, 241, 0.12)',
    },
  },
  orange: {
    colorPrimary: '#fb923c',
    colorInfo: '#fdba74',
    menu: {
      itemSelectedBg: 'rgba(251, 146, 60, 0.32)',
      itemSelectedColor: '#fdba74',
      itemHoverBg: 'rgba(251, 146, 60, 0.14)',
    },
  },
  sky: {
    colorPrimary: '#38bdf8',
    colorInfo: '#22d3ee',
    menu: {
      itemSelectedBg: 'rgba(56, 189, 248, 0.32)',
      itemSelectedColor: '#7dd3fc',
      itemHoverBg: 'rgba(56, 189, 248, 0.14)',
    },
  },
  rose: {
    colorPrimary: '#fb7185',
    colorInfo: '#f472b6',
    menu: {
      itemSelectedBg: 'rgba(251, 113, 133, 0.32)',
      itemSelectedColor: '#fda4af',
      itemHoverBg: 'rgba(251, 113, 133, 0.14)',
    },
  },
} satisfies typeof lightAccent;

function buildLightTheme(accent: ThemeAccent): ThemeConfig {
  const a = lightAccent[accent];
  return {
    token: {
      ...lightNeutralToken,
      colorPrimary: a.colorPrimary,
      colorInfo: a.colorInfo,
    },
    components: {
      Layout: {
        headerBg: '#ffffff',
        siderBg: '#ffffff',
        bodyBg: '#f1f5f9',
      },
      Menu: {
        itemBg: 'transparent',
        itemSelectedBg: a.menu.itemSelectedBg,
        itemSelectedColor: a.menu.itemSelectedColor,
        itemHoverBg: a.menu.itemHoverBg,
      },
      Card: {
        colorBgContainer: '#ffffff',
      },
    },
  };
}

function buildDarkTheme(accent: ThemeAccent): ThemeConfig {
  const a = darkAccent[accent];
  return {
    token: {
      ...darkNeutralToken,
      colorPrimary: a.colorPrimary,
      colorInfo: a.colorInfo,
    },
    components: {
      Layout: {
        headerBg: '#1e293b',
        siderBg: '#1e293b',
        bodyBg: '#0f172a',
      },
      Menu: {
        itemBg: 'transparent',
        itemSelectedBg: a.menu.itemSelectedBg,
        itemSelectedColor: a.menu.itemSelectedColor,
        itemHoverBg: a.menu.itemHoverBg,
      },
      Card: {
        colorBgContainer: '#1e293b',
      },
    },
  };
}

export const themesByMode: Record<ThemeMode, ThemeConfig> = {
  light: buildLightTheme('blue'),
  lightOrange: buildLightTheme('orange'),
  lightSky: buildLightTheme('sky'),
  lightRose: buildLightTheme('rose'),
  dark: buildDarkTheme('blue'),
  darkOrange: buildDarkTheme('orange'),
  darkSky: buildDarkTheme('sky'),
  darkRose: buildDarkTheme('rose'),
};

export const lightTheme = themesByMode.light;
export const darkTheme = themesByMode.dark;

/** CSS `background` values for Settings palette swatches (aligned with token colors). */
function swatchBackgroundLight(accent: ThemeAccent): string {
  const a = lightAccent[accent];
  return `linear-gradient(145deg, ${a.colorPrimary} 0%, ${a.colorInfo} 50%, #ffffff 100%)`;
}

function swatchBackgroundDark(accent: ThemeAccent): string {
  const a = darkAccent[accent];
  return `linear-gradient(145deg, #0f172a 0%, ${darkNeutralToken.colorBgContainer} 45%, ${a.colorPrimary} 100%)`;
}

export const THEME_MODE_SWATCH: Record<ThemeMode, string> = {
  light: swatchBackgroundLight('blue'),
  lightOrange: swatchBackgroundLight('orange'),
  lightSky: swatchBackgroundLight('sky'),
  lightRose: swatchBackgroundLight('rose'),
  dark: swatchBackgroundDark('blue'),
  darkOrange: swatchBackgroundDark('orange'),
  darkSky: swatchBackgroundDark('sky'),
  darkRose: swatchBackgroundDark('rose'),
};
